// -----------------------------------------------------------------------------
// src/components/AppShell.jsx   — improved 2025-07-18 with menu reorganization
// -----------------------------------------------------------------------------
import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from "react";
import {
  Box, Paper, IconButton, Menu, MenuItem, Tooltip,
  useMediaQuery, Badge,
} from "@mui/material";
import IosShareIcon                from "@mui/icons-material/IosShare";
import MenuIcon                    from "@mui/icons-material/Menu";
import HomeOutlined                from "@mui/icons-material/HomeOutlined";
import ChatBubbleOutlineOutlined   from "@mui/icons-material/ChatBubbleOutlineOutlined";
import HandshakeOutlined           from "@mui/icons-material/HandshakeOutlined";
import InsertDriveFileOutlined     from "@mui/icons-material/InsertDriveFileOutlined";
import PeopleOutline               from "@mui/icons-material/PeopleOutline";
import SupervisorAccountOutlined   from "@mui/icons-material/SupervisorAccountOutlined";
import AttachMoneyIcon             from "@mui/icons-material/AttachMoney";
import AccountCircleIcon           from "@mui/icons-material/AccountCircle";
import AppsIcon                    from "@mui/icons-material/Apps";
import DashboardIcon               from "@mui/icons-material/Dashboard";
import StorefrontOutlined          from "@mui/icons-material/StorefrontOutlined";
import { Share }                   from "@capacitor/share";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useQuery }                from "react-query";

import routes            from "./Config/routes";
import useAuth           from "./Hooks/use-auth";
import { useCreatorAuth } from "./Hooks/creator-use-auth";
import client            from "./API";

/*────────────────────────────────────────────────────────────────────────────*/
const WEB_BASE      = "https://www.useblitz.co";
const HIDE_AFTER    = 2500;      // ms until bar fades
const BAR_HEIGHT    = 86;        // px – 78px + 10% height boost
const ICON_SIZE     = "large";
const PAY_SIZE      = 60;        // px – primary FAB (Pay button)
const MAX_MAIN_BTNS = 4;         // Home/Dash, Chat, Pay, +1 extra
const EXTRA_GUTTER  = 12;   

// Swipe configuration
const SWIPE_THRESHOLD = 50;      // minimum distance for swipe
const SWIPE_VELOCITY_THRESHOLD = 0.3;  // minimum velocity for swipe
const SWIPE_MAX_TIME = 300;      // maximum time for swipe gesture (ms)
/*────────────────────────────────────────────────────────────────────────────*/

export default function AppShell() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const pathname   = location.pathname;
  const search     = location.search;

  const isMobile   = useMediaQuery((t) => t.breakpoints.down("md"));
  const isNative   = Boolean(window.Capacitor?.isNativePlatform?.());

  /*──────── auth */
  const { isAuthorized, getCurrrentUser } = useAuth();
  const user                 = getCurrrentUser?.();
  const { creatorToken }     = useCreatorAuth();
  const isCreatorAuthorized  = Boolean(creatorToken) && !isAuthorized;

  /*──────── scroll & fade management */
  const [visible, setVisible] = useState(true);
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const [isScrolling, setIsScrolling] = useState(false);
  const timerRef = useRef();
  const scrollTimeoutRef = useRef();
  const lastScrollY = useRef(0);

  /*──────── swipe navigation state */
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartTime, setTouchStartTime] = useState(null);
  const [isVerticalScroll, setIsVerticalScroll] = useState(false);

  const resetTimer = useCallback(() => {
    setVisible(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setVisible(false), HIDE_AFTER);
  }, []);

  // Enhanced scroll handler for transparency
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const scrollDelta = Math.abs(currentScrollY - lastScrollY.current);
    
    // Mark as scrolling
    setIsScrolling(true);
    clearTimeout(scrollTimeoutRef.current);
    
    // Calculate opacity based on scroll speed and position
    const scrollSpeed = scrollDelta;
    let newOpacity = 1;
    
    if (scrollSpeed > 5) {
      // Fast scrolling - more transparent
      newOpacity = 0.3;
    } else if (scrollSpeed > 2) {
      // Medium scrolling - semi-transparent
      newOpacity = 0.6;
    } else if (currentScrollY > 50) {
      // Scrolled down but slow - slight transparency
      newOpacity = 0.85;
    }
    
    setScrollOpacity(newOpacity);
    lastScrollY.current = currentScrollY;
    
    // Reset to full opacity after scrolling stops
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
      setScrollOpacity(1);
    }, 150);
    
    // Also reset the visibility timer
    resetTimer();
  }, [resetTimer]);

  /*──────── swipe detection handlers */
  const handleTouchStart = useCallback((e) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
    setTouchStartTime(Date.now());
    setIsVerticalScroll(false);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStart) return;

    const currentTouch = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    };

    const deltaX = Math.abs(currentTouch.x - touchStart.x);
    const deltaY = Math.abs(currentTouch.y - touchStart.y);

    // If vertical movement is greater than horizontal, treat as scroll
    if (deltaY > deltaX && deltaY > 10) {
      setIsVerticalScroll(true);
    }
  }, [touchStart]);

  const handleTouchEnd = useCallback((e) => {
    if (!touchStart || !e.changedTouches[0]) return;

    const touchEndPos = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };
    
    setTouchEnd(touchEndPos);

    const deltaX = touchStart.x - touchEndPos.x;
    const deltaY = Math.abs(touchStart.y - touchEndPos.y);
    const distance = Math.abs(deltaX);
    const time = Date.now() - touchStartTime;
    const velocity = distance / time;

    // Don't process swipe if it was vertical scrolling
    if (isVerticalScroll) return;

    // Check if swipe meets criteria
    const isValidSwipe = 
      distance > SWIPE_THRESHOLD && 
      velocity > SWIPE_VELOCITY_THRESHOLD && 
      time < SWIPE_MAX_TIME &&
      deltaY < 100; // Ensure it's mostly horizontal

    if (isValidSwipe) {
      if (deltaX > 0) {
        // Swipe left - go forward
        try {
          navigate(1);
        } catch (error) {
          console.log("Cannot navigate forward");
        }
      } else {
        // Swipe right - go back
        try {
          navigate(-1);
        } catch (error) {
          console.log("Cannot navigate back");
        }
      }
    }

    // Reset swipe state
    setTouchStart(null);
    setTouchEnd(null);
    setTouchStartTime(null);
    setIsVerticalScroll(false);
  }, [touchStart, touchStartTime, isVerticalScroll, navigate]);

  useEffect(() => {
    resetTimer();
    const events = ["touchstart", "mousedown", "keydown"];
    const scrollEvents = ["scroll", "touchmove"];
    
    events.forEach((e) =>
      window.addEventListener(e, resetTimer, { passive: true })
    );
    
    scrollEvents.forEach((e) =>
      window.addEventListener(e, handleScroll, { passive: true })
    );

    // Add swipe event listeners
    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      scrollEvents.forEach((e) => window.removeEventListener(e, handleScroll));
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      clearTimeout(timerRef.current);
      clearTimeout(scrollTimeoutRef.current);
    };
  }, [resetTimer, handleScroll, handleTouchStart, handleTouchMove, handleTouchEnd]);

  /*──────── share */
  const handleShare = async () => {
    const canonical = `${WEB_BASE}${location.pathname}${location.search}${location.hash}`;
    try {
      await Share.share({ title: document.title || "Share", url: canonical });
    } catch (err) {
      console.error("Share failed:", err);
    }
  };

  /*──────── queries (unread counts) */
  const { data: dashboardData = {} } = useQuery(
    ["userDashboard"],
    () => client.users.dashboard(),
    { enabled: isAuthorized, staleTime: 300_000 },
  );
  const unreadConversationCount =
    dashboardData.unread_conversations_count ?? 0;

  const { data: unreadCollabData = {} } = useQuery(
    ["notifications", "unread_collab_count"],
    () => client.notifications.getUnreadCollabCount(),
    { enabled: isAuthorized, staleTime: 300_000 },
  );
  const unreadCollabCount = unreadCollabData.unread_collab_count ?? 0;

  // Company data for user access control
  const { data: companyData = {} } = useQuery(
    ["company", user?.company_id],
    () => client.companies.listFetch({ id: user?.company_id }),
    { enabled: isAuthorized && !!user?.company_id, staleTime: 300_000 }
  );

  const creatorUsername = creatorToken?.creator_user?.username;
  const { data: creatorConvos = [] } = useQuery(
    ["creatorConversations", creatorUsername],
    () => client.conversations.fetchCreatorConversations(creatorUsername),
    { enabled: isCreatorAuthorized },
  );
  const unreadCreatorChatCount = useMemo(
    () => creatorConvos.reduce((a, c) => a + (Number(c.unread_count) || 0), 0),
    [creatorConvos],
  );

  /*──────── nav button sets */
  const userBtns = [
    { k: "home",  r: routes.dashboard,               i: <HomeOutlined /> },
    {
      k: "chat",
      r: routes.conversationshub,
      i: (
        <Badge
          badgeContent={unreadConversationCount}
          color="secondary"
          max={99}
          invisible={!unreadConversationCount}
        >
          <ChatBubbleOutlineOutlined />
        </Badge>
      ),
    },
    {
      k: "pay",
      r: routes.blitzpay,
      i: <AttachMoneyIcon fontSize="inherit" />,
      big: true,
    },
    {
      k: "collab",
      r: routes.collaborations,
      i: (
        <Badge
          badgeContent={unreadCollabCount}
          color="secondary"
          max={99}
          invisible={!unreadCollabCount}
        >
          <HandshakeOutlined />
        </Badge>
      ),
    },
  ];

  const creatorBtns = [
    { k: "dash", r: routes.creatorConnectStart, i: <DashboardIcon /> },
    {
      k: "chat",
      r: routes.creatorConvos,
      i: (
        <Badge
          badgeContent={unreadCreatorChatCount}
          color="secondary"
          max={99}
          invisible={!unreadCreatorChatCount}
        >
          <ChatBubbleOutlineOutlined />
        </Badge>
      ),
    },
    {
      k: "pay",
      r: routes.creatorConnectPayoutsInvoicing,
      i: <AttachMoneyIcon fontSize="inherit" />,
      big: true,
    },
    {
      k: "collab",
      r: routes.creatorCollabs,
      i: <HandshakeOutlined />,
    },
    { k: "bio", r: routes.creatorbio, i: <AccountCircleIcon /> },
  ];

  const btns         = isAuthorized
    ? userBtns
    : isCreatorAuthorized
    ? creatorBtns
    : [];
  const mainBtns     = btns.slice(0, MAX_MAIN_BTNS);
  const overflowBtns = btns.slice(MAX_MAIN_BTNS);

  /*──────── menu items */
  const menuItems = useMemo(() => {
    const items = [];
    
    // Share button always first in menu
    items.push({ 
      t: "Share", 
      r: null, 
      i: <IosShareIcon />, 
      action: handleShare 
    });

    // Add overflow buttons from main nav
    const extraMenuItems = overflowBtns.map(({ k, r, i }) => ({
      t: k.charAt(0).toUpperCase() + k.slice(1),
      r,
      i,
    }));
    items.push(...extraMenuItems);

    // User-specific menu items
    if (isAuthorized) {
      items.push({ t: "Marketplace", r: routes.search, i: <StorefrontOutlined /> });
      items.push({ t: "Files", r: routes.files, i: <InsertDriveFileOutlined /> });
      
      // My Agency (for Agency and AlphaFree accounts)
      if (
        companyData?.account_status === "Agency" ||
        companyData?.account_status === "AlphaFree"
      ) {
        items.push({ t: "My Agency", r: routes.agency, i: <PeopleOutline /> });
      }
      
      // Account Manager (if user has supervisor)
      if (user?.supervisor) {
        items.push({ t: "Account Management", r: routes.amDash, i: <SupervisorAccountOutlined /> });
      }
      
      // Admin (for admin users)
      if (user?.type === "admin") {
        items.push({ t: "Admin", r: routes.company, i: <SupervisorAccountOutlined /> });
      }
    } else if (isCreatorAuthorized) {
      // Creator-specific menu items
      items.push({ t: "App Store", r: routes.creatorConnectApps, i: <AppsIcon /> });
    }
    
    return items;
  }, [isAuthorized, isCreatorAuthorized, user, companyData, overflowBtns, handleShare]);

  const [anchorEl, setAnchorEl] = useState(null);
  const safeBottom = "env(safe-area-inset-bottom,0px)";

  /*──────── nav-visibility helpers */
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const isChatScreen = useMemo(
    () =>
      pathname.includes("/convo/") ||
      qs.has("conversation") ||
      qs.has("with"),
    [pathname, qs],
  );

  const showBottomNav = useMemo(() => {
    if (isChatScreen) return false;
    if (isMobile) return true;
    /* desktop hubs */
    return (
      pathname.startsWith("/convos/") ||
      pathname.startsWith("/creatorconnect/convos/")
    );
  }, [isChatScreen, isMobile, isNative, pathname]);

  /*──────── helpers */
  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (item) => {
    handleMenuClose();
    if (item.action) {
      item.action();
    } else if (item.r) {
      navigate(item.r);
    }
  };

  // Calculate final opacity (combination of visibility and scroll opacity)
  const finalOpacity = visible ? scrollOpacity : 0;

  /*───────────────── render ─────────────────*/
  return (
    <Box
      sx={{
        pb: safeBottom,
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
      }}
    >
      {/* main routed content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          pb: showBottomNav
            ? `calc(${BAR_HEIGHT + EXTRA_GUTTER}px + env(safe-area-inset-bottom,0px))`
            : 0,
        }}
      >
        <Outlet />
      </Box>

      {showBottomNav && (
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            height: BAR_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center", // Changed to center since no share button
            px: 2,
            bgcolor: "background.paper",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            transition: "opacity 250ms ease-out",
            opacity: finalOpacity,
            pointerEvents: visible ? "auto" : "none",
            zIndex: (theme) => theme.zIndex.modal + 2,
            // Add backdrop blur for better readability when transparent
            backdropFilter: isScrolling ? "blur(8px)" : "none",
            WebkitBackdropFilter: isScrolling ? "blur(8px)" : "none",
          }}
        >
          {/* central nav & overflow menu */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              flex: 1,
            }}
          >
            {mainBtns.map(({ k, i, r, big }) => (
              <Tooltip key={k} title={k.charAt(0).toUpperCase() + k.slice(1)}>
                <IconButton
                  size={ICON_SIZE}
                  onClick={() => navigate(r)}
                  sx={
                    big
                      ? {
                          width: PAY_SIZE,
                          height: PAY_SIZE,
                          borderRadius: "50%",
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          boxShadow: 4,
                          "&:hover": { bgcolor: "primary.dark" },
                        }
                      : undefined
                  }
                >
                  {i}
                </IconButton>
              </Tooltip>
            ))}

            {menuItems.length > 0 && (
              <>
                <Tooltip title="Menu">
                  <IconButton
                    size={ICON_SIZE}
                    onMouseDown={handleMenuOpen}
                    onClick={handleMenuOpen}
                  >
                    <MenuIcon />
                  </IconButton>
                </Tooltip>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  // Improved positioning to prevent cutoff
                  anchorOrigin={{ 
                    vertical: "top", 
                    horizontal: "center" 
                  }}
                  transformOrigin={{ 
                    vertical: "bottom", 
                    horizontal: "center" 
                  }}
                  // Better positioning and styling
                  slotProps={{
                    paper: {
                      sx: {
                        maxHeight: "70vh",
                        minWidth: 200,
                        overflowY: "auto",
                        mt: -1, // Move menu up slightly to prevent overlap
                        mb: 1,  // Ensure space from bottom
                        boxShadow: 6,
                        borderRadius: 2,
                        // Ensure menu doesn't get cut off by viewport
                        maxWidth: "90vw",
                        // Better backdrop for readability
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        backgroundColor: (theme) => 
                          theme.palette.mode === 'dark' 
                            ? 'rgba(18, 18, 18, 0.95)' 
                            : 'rgba(255, 255, 255, 0.95)',
                      }
                    }
                  }}
                  // Prevent scrolling issues
                  disableScrollLock
                  // Ensure proper positioning within viewport
                  disablePortal={false}
                  // Additional props for better mobile experience
                  keepMounted={false}
                >
                  {menuItems.map(({ t, r, i, action }) => (
                    <MenuItem
                      key={t}
                      onClick={() => handleMenuItemClick({ t, r, i, action })}
                      sx={{
                        py: 1.5,
                        px: 2,
                        minHeight: 48,
                        "&:hover": {
                          backgroundColor: (theme) => 
                            theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.1)' 
                              : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <Box sx={{ mr: 2, display: "flex", alignItems: "center" }}>
                        {i}
                      </Box>
                      {t}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
}